import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";

export type PortfolioTabKey = "list" | "chart";
export type PortfolioScope = "running" | "all";

export const PORTFOLIO_PRESETS = ["1D", "3D", "1W", "1M"] as const;
export type PortfolioPreset = (typeof PORTFOLIO_PRESETS)[number];

export interface PortfolioUrlState {
  tab: PortfolioTabKey;
  // When `preset` is set, `from` / `to` are derived from it against the current time.
  // When `preset` is null, `from` / `to` carry the explicit values from the URL;
  // an empty `to` (no `to` in the URL) means "up to now".
  preset: PortfolioPreset | null;
  from: string;
  to: string;
  executionId: number | null;
  // Top metrics strip follows the selected dates (true) or shows lifetime figures (false).
  sync: boolean;
  scope: PortfolioScope;
}

const URL_KEYS = {
  TAB: "tab",
  PRESET: "preset",
  FROM: "from",
  TO: "to",
  EXEC: "exec",
  SYNC: "sync",
  SCOPE: "scope",
} as const;

// Datetime format aligned with <input type="datetime-local"> — "YYYY-MM-DDTHH:mm".
// All values are UTC (backend timestamps are UTC).
export const PORTFOLIO_DATETIME_FORMAT = "YYYY-MM-DDTHH:mm";
const DEFAULT_PRESET: PortfolioPreset = "1D";

const PRESET_HOURS: Record<PortfolioPreset, number> = {
  "1D": 24,
  "3D": 72,
  "1W": 24 * 7,
  "1M": 24 * 30,
};

// A preset, and an explicit range with no end, are rolled against `nowUtc`.
export const resolvePortfolioRange = (
  preset: PortfolioPreset | null,
  from: string,
  to: string,
  nowUtc: dayjs.Dayjs,
): { from: string; to: string } => {
  const now = nowUtc.format(PORTFOLIO_DATETIME_FORMAT);
  if (preset === null) return { from, to: to || now };
  return {
    from: nowUtc.subtract(PRESET_HOURS[preset], "hour").format(PORTFOLIO_DATETIME_FORMAT),
    to: now,
  };
};

const isValidTab = (value: string | null): value is PortfolioTabKey =>
  value === "list" || value === "chart";

const isValidPreset = (value: string | null): value is PortfolioPreset =>
  value !== null && (PORTFOLIO_PRESETS as readonly string[]).includes(value);

const isValidDate = (value: string | null): value is string =>
  !!value && dayjs.utc(value).isValid();

export const usePortfolioUrlState = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo<PortfolioUrlState>(() => {
    const tabParam = searchParams.get(URL_KEYS.TAB);
    const presetParam = searchParams.get(URL_KEYS.PRESET);
    const fromParam = searchParams.get(URL_KEYS.FROM);
    const toParam = searchParams.get(URL_KEYS.TO);
    const execParam = searchParams.get(URL_KEYS.EXEC);

    const execNum = execParam ? Number(execParam) : NaN;

    // A valid `from` with no `to` is an explicit range that ends now.
    const hasExplicitRange = isValidDate(fromParam) && (toParam === null || isValidDate(toParam));
    // Unknown / malformed preset collapses to the default rather than
    // leaving an empty range that would silently produce an empty result.
    const preset = hasExplicitRange
      ? null
      : isValidPreset(presetParam)
        ? presetParam
        : DEFAULT_PRESET;

    return {
      tab: isValidTab(tabParam) ? tabParam : "list",
      preset,
      from: hasExplicitRange ? (fromParam as string) : "",
      to: hasExplicitRange ? (toParam ?? "") : "",
      executionId: Number.isFinite(execNum) && execNum > 0 ? execNum : null,
      sync: searchParams.get(URL_KEYS.SYNC) !== "0",
      scope: searchParams.get(URL_KEYS.SCOPE) === "all" ? "all" : "running",
    };
  }, [searchParams]);

  const setTab = useCallback(
    (tab: PortfolioTabKey) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set(URL_KEYS.TAB, tab);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setPreset = useCallback(
    (preset: PortfolioPreset) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set(URL_KEYS.PRESET, preset);
          params.delete(URL_KEYS.FROM);
          params.delete(URL_KEYS.TO);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setRange = useCallback(
    // `to` null keeps the range ending now.
    (from: string, to: string | null) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set(URL_KEYS.FROM, from);
          if (to === null) params.delete(URL_KEYS.TO);
          else params.set(URL_KEYS.TO, to);
          params.delete(URL_KEYS.PRESET);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setExecutionId = useCallback(
    (executionId: number | null) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (executionId === null) params.delete(URL_KEYS.EXEC);
          else params.set(URL_KEYS.EXEC, String(executionId));
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setSync = useCallback(
    (sync: boolean) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (sync) params.delete(URL_KEYS.SYNC);
          else params.set(URL_KEYS.SYNC, "0");
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setScope = useCallback(
    (scope: PortfolioScope) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (scope === "running") params.delete(URL_KEYS.SCOPE);
          else params.set(URL_KEYS.SCOPE, scope);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return {
    ...state,
    setTab,
    setPreset,
    setRange,
    setExecutionId,
    setSync,
    setScope,
  };
};
